import React, { useEffect, useState } from 'react';
import { Box, Flex } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';

import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import dataLayer from '@/v2/utils/dataLayer';
import useForm from '../../../../hooks/useForm';
import {
  isEmail,
  isLength,
  isMobilePhone,
  isNotEmpty,
  isQFFOrEmpty,
} from '../../../../lib/validator';
import Button from '../../../Button';
import FormControl from '../../../FormControl';
import Input from '../../../Input';
import Textarea from '../../../Textarea';
import TitleSelect from '../../../TitleSelect';
import withProps from '../../../withProps';

const FormColumn = withProps({
  width: ['100%', null, 'auto'],
  flex: [null, null, '1'],
  p: '2',
  labelProps: {
    fontWeight: '300',
    mb: '1',
  },
})(FormControl);

const FormFlex = withProps({
  flexWrap: 'wrap',
  m: -2,
  py: '2',
})(Flex);

const Form = ({ data, onSubmit }) => {
  const shouldUseNewEvents = useGA4Events();
  const [formStartEventSent, setFormStartEventSent] = useState(false);
  const {
    getValue,
    getError,
    handleValueChange,
    handleSubmit,
    isFormDirty,
    validate,
  } = useForm(data, onSubmit);

  useEffect(() => {
    if (isFormDirty && !formStartEventSent && shouldUseNewEvents) {
      dataLayer.contactUsFormEvent({ action: 'start' });
      setFormStartEventSent(true);
    }
  }, [formStartEventSent, isFormDirty, shouldUseNewEvents]);

  const submit = (event) => {
    if (!!validate() && shouldUseNewEvents) {
      dataLayer.contactUsFormEvent({ action: 'complete' });
    }
    handleSubmit(event);
  };

  return (
    <form onSubmit={(event) => submit(event)}>
      <Box fontSize="lg" mt="10" mb="4">
        Your contact details
      </Box>
      <FormFlex>
        <FormColumn
          width={['100%', null, '150px']}
          label="Title"
          htmlFor="contact_us_title"
        >
          <TitleSelect
            error={getError('title', { onFormDirty: true })}
            data-testid="TITLE"
            id="contact_us_title"
            onChange={handleValueChange('title', [
              isNotEmpty,
              'Title is required',
            ])}
            value={getValue('title')}
          />
        </FormColumn>
        <FormColumn label="Given names" htmlFor="contact_us_given_names">
          <Input
            data-testid="GIVEN_NAMES"
            id="contact_us_given_names"
            error={getError('givenNames', { onFormDirty: true })}
            onChange={handleValueChange('givenNames', [
              [isNotEmpty, 'Given names is required'],
              [
                isLength,
                'Given names should be less than 80 characters',
                { max: 80 },
              ],
            ])}
            value={getValue('givenNames')}
          />
        </FormColumn>
        <FormColumn label="Last name" htmlFor="contact_us_last_name">
          <Input
            data-testid="LAST_NAME"
            id="contact_us_last_name"
            error={getError('lastName', { onFormDirty: true })}
            onChange={handleValueChange('lastName', [
              [isNotEmpty, 'Last name is required'],
              [
                isLength,
                'Last name should be less than 80 characters',
                { max: 80 },
              ],
            ])}
            value={getValue('lastName')}
          />
        </FormColumn>
      </FormFlex>
      <FormFlex>
        <FormColumn label="Email address" htmlFor="contact_us_email_address">
          <Input
            data-testid="EMAIL_ADDRESS"
            error={getError('emailAddress', { onFormDirty: true })}
            id="contact_us_email_address"
            onChange={handleValueChange('emailAddress', [
              isEmail,
              'Please enter a valid email address',
            ])}
            value={getValue('emailAddress')}
          />
        </FormColumn>
        <FormColumn label="Phone" htmlFor="contact_us_phone">
          <Input
            data-testid="PHONE"
            error={getError('phone', { onFormDirty: true })}
            id="contact_us_phone"
            onChange={handleValueChange('phone', [
              isMobilePhone,
              'Please input a valid phone number',
            ])}
            value={getValue('phone')}
          />
        </FormColumn>
      </FormFlex>
      <FormFlex>
        <FormColumn
          optional
          label="Booking reference"
          htmlFor="contact_us_booking_reference"
        >
          <Input
            data-testid="BOOKING_REFERENCE"
            error={getError('bookingReference', { onFormDirty: true })}
            id="contact_us_booking_reference"
            onChange={handleValueChange('bookingReference', [
              isLength,
              'Please input a valid booking reference',
              { max: 10 },
            ])}
            value={getValue('bookingReference')}
          />
        </FormColumn>
        <FormColumn
          optional
          label="Qantas Frequent Flyer Number"
          htmlFor="contact_us_qantas_frequent_flyer_number"
        >
          <Input
            data-testid="QFF_NUMBER"
            error={getError('qantasFrequentFlyerNumber', { onFormDirty: true })}
            id="contact_us_qantas_frequent_flyer_number"
            onChange={handleValueChange('qantasFrequentFlyerNumber', [
              isQFFOrEmpty,
              'Please enter a valid Frequent Flyer Number',
            ])}
            value={getValue('qantasFrequentFlyerNumber')}
          />
        </FormColumn>
      </FormFlex>
      <Box fontSize="lg" mt="10" mb="4">
        Your enquiry
      </Box>
      <FormFlex>
        <FormColumn label="Message" htmlFor="contact_us_message">
          <Textarea
            data-testid="MESSAGE"
            error={getError('message', { onFormDirty: true })}
            id="contact_us_message"
            onChange={handleValueChange('message', [
              isNotEmpty,
              'Your Message is required',
            ])}
            value={getValue('message')}
            characterLimit={1000}
          />
        </FormColumn>
      </FormFlex>
      <Box mt="4">
        <Button variant="primary">Submit Support enquiry</Button>
      </Box>
    </form>
  );
};

Form.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  data: PropTypes.shape({
    title: PropTypes.string,
    givenNames: PropTypes.string,
    lastName: PropTypes.string,
    emailAddress: PropTypes.string,
    phone: PropTypes.string,
    bookingReference: PropTypes.string,
    qantasFrequentFlyerNumber: PropTypes.string,
    message: PropTypes.string,
  }),
};

export default Form;
